import { memo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useInView } from 'react-intersection-observer';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';
import { trans, useSoyuzExperiment } from '@spotahome/soyuz/client';

import {
  DefaultHomecard,
  HOMECARD_IMAGE_RATIOS,
  isPlusPlan
} from '@spotahome/ui-library';
import { getListingChips } from '@spotahome/ui-library/src/utils/listing';
import { FavoriteProvider } from '@spotahome/ui-library/src/tenant/context/FavoriteContext';
import {
  formatHomecardDate,
  formatHomecardDateFullMonthYear,
  formatHomecardDateFullMonth
} from '@spotahome/ui-library/src/tenant/components/utils/dateUtils';

import { getTranslatedPropertyType } from '@spotahome/ui-library/src/tenant/data/common/property';

import {
  getHomecardImageSet,
  getRatingFromReviews,
  getUrlWithMovingDates,
  getBestOfferDiscount
} from '../../utils/homecards';

import { getRentableUnitPrice } from '../../utils/paymentsAdapter';

import { generateHomeCardLabels } from './generateHomeCardLabels';

const MAX_CHIPS_TO_SHOW = 2;

const noop = () => {};

const getHomecardClasses = (visited, selected, tracking) =>
  classNames(
    'home-card',
    'is-animated',
    {
      'home-card--visited': visited,
      'home-card--selected': selected
    },
    tracking
  );

const parseAvailableFrom = (
  availableFrom,
  showAvailableFromYear,
  isSimplifyHomecardsDesignVariant
) => {
  if (isSimplifyHomecardsDesignVariant) {
    return formatHomecardDate(availableFrom);
  }

  if (showAvailableFromYear) {
    return formatHomecardDateFullMonthYear(availableFrom);
  }

  return formatHomecardDateFullMonth(availableFrom);
};

const composeSEOTitle = (propertyType, street, city) =>
  trans('homecard.seo.title', {
    propertyType: getTranslatedPropertyType(propertyType),
    street,
    city
  });

const Homecard = ({
  data: homecard,
  moveIn = '',
  moveOut = '',
  flexibleMoveIn = false,
  flexibleMoveOut = false,
  onCardMouseEnter = noop,
  onCardMouseLeave = noop,
  onClick = noop,
  selected = false,
  tracking = '',
  hasLazyLoad = false,
  renderExtraContent = noop,
  newTab = true,
  showAvailableFromYear = false,
  withTransparentBackground = false,
  onImageChange = noop,
  showDescription = false,
  isBooked = false,
  pageType = '',
  section = '',
  showFavorite = true,
  linkTo = null,
  showSquaredMeters = false,
  imageRatio = HOMECARD_IMAGE_RATIOS.HOMECARD,
  isEmbedded = false,
  dataTest = '',
  imageClassName = '',
  contentClassName = '',
  renderRightFloatingElements = noop
}) => {
  const SimplifyHomecardsDesign = useSoyuzExperiment('SimplifyHomecardsDesign');

  const price = getRentableUnitPrice(
    homecard.monthlyPrice.type,
    homecard.monthlyPrice.minimumPrice,
    homecard.currencyIsoCode,
    homecard.monthlyPrice.pricesByMonth,
    homecard.monthlyPrice.fixedPrice,
    { moveIn, moveOut },
    {
      flexibleMoveIn,
      flexibleMoveOut
    }
  );

  const hasDepositExempt = homecard.noSecurityDeposit === true;

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  useEffect(() => {
    if (inView && isBooked) {
      SoyuzAnalytics.sendGA4Event('show', {
        section: 'soldout-homecard'
      });
    }
  }, [inView]);

  const isPlus = isPlusPlan(homecard.plan);

  const handleMouseEnter = () => {
    onCardMouseEnter(homecard.id);
  };

  const handleMouseLeave = () => {
    onCardMouseLeave(homecard.id);
  };

  const classes = getHomecardClasses(homecard.visited, selected, tracking);

  const homecardBottomChips = getListingChips(
    hasDepositExempt,
    homecard.isInstantBooking,
    homecard.billsIncluded,
    homecard.isBestChoice,
    isPlus,
    homecard.allowsPermanentContract
  ).slice(0, MAX_CHIPS_TO_SHOW);

  const onMoreInfoClickHandler = () => {
    SoyuzAnalytics.sendGA4Event('click_more_info_homecard', {});
  };

  const isSimplifyHomecardsDesignVariant =
    !isBooked && SimplifyHomecardsDesign.get('behaviour') === 'variant';

  const photos = homecard.photos;
  return (
    <div className={classes} ref={ref}>
      <FavoriteProvider
        listingId={homecard.id}
        cityId={homecard.city}
        propertyType={homecard.type}
        price={homecard.displayPrice}
        isFavorite={homecard.isFavorite}
        favoritesCount={homecard.favoritesCount}
      >
        <DefaultHomecard
          price={price}
          availableFrom={parseAvailableFrom(
            homecard.firstAvailableDate,
            showAvailableFromYear,
            isSimplifyHomecardsDesignVariant
          )}
          image={homecard.mainPhotoUrl}
          imageSet={
            homecard.photoUrls ? getHomecardImageSet(homecard.photoUrls) : ''
          }
          link={linkTo ?? getUrlWithMovingDates(homecard.url, moveIn, moveOut)}
          onClick={onClick}
          onMouseEnter={handleMouseEnter} // TODO: this might be treated in the patternLibrary
          onMouseLeave={handleMouseLeave} // TODO: this might be treated in the patternLibrary
          rating={getRatingFromReviews(homecard.reviews)}
          renderLabels={generateHomeCardLabels(homecard)}
          selected={selected} // TODO: this might be treated in the patternLibrary
          title={
            isSimplifyHomecardsDesignVariant
              ? composeSEOTitle(
                  homecard.type,
                  homecard.location.street,
                  homecard.location.city
                )
              : homecard.title
          }
          type={homecard.type}
          verified={homecard.checked}
          hasLazyLoad={hasLazyLoad}
          isSelect={homecard.isSelect}
          renderExtraContent={renderExtraContent}
          showAvailableFromYear={showAvailableFromYear}
          newTab={newTab}
          photos={photos}
          chips={homecardBottomChips}
          withTransparentBackground={withTransparentBackground}
          offerDiscount={getBestOfferDiscount(
            homecard.offers,
            homecard.currencyIsoCode
          )}
          description={
            showDescription
              ? {
                  content: homecard.description,
                  area: homecard.area,
                  numberOfBedrooms: homecard.numberOfBedrooms,
                  numberOfBathrooms: homecard.numberOfBathrooms,
                  features: homecard.features,
                  type: homecard.type,
                  roomArea: homecard.roomArea
                }
              : null
          }
          descriptionPosition="bottom"
          onMoreInfoClick={onMoreInfoClickHandler}
          onImageChange={onImageChange}
          isBooked={isBooked}
          showFavorite={showFavorite}
          pageType={pageType}
          section={section}
          area={homecard.area}
          roomArea={homecard.roomArea}
          showSquaredMeters={showSquaredMeters}
          imageRatio={imageRatio}
          isEmbedded={isEmbedded}
          dataTest={dataTest}
          scoring={homecard.landlordScoring}
          imageClassName={imageClassName}
          contentClassName={contentClassName}
          renderRightFloatingElements={renderRightFloatingElements}
        />
      </FavoriteProvider>
    </div>
  );
};

Homecard.propTypes = {
  data: PropTypes.shape({
    adId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    checked: PropTypes.bool,
    id: PropTypes.string,
    city: PropTypes.string,
    isFavorite: PropTypes.bool,
    favoritesCount: PropTypes.number,
    mainPhotoUrl: PropTypes.string,
    monthlyPrice: PropTypes.shape({
      type: PropTypes.string,
      minimumPrice: PropTypes.number,
      pricesByMonth: PropTypes.shape({
        [PropTypes.string]: PropTypes.number
      }),
      fixedPrice: PropTypes.number
    }),
    currencyIsoCode: PropTypes.string,
    features: PropTypes.shape({ [PropTypes.string]: PropTypes.bool }),
    noSecurityDeposit: PropTypes.bool,
    photos: PropTypes.arrayOf(PropTypes.shape({ src: PropTypes.string })),
    photoUrls: PropTypes.shape({
      homecardHidpi: PropTypes.string,
      homecard: PropTypes.string
    }),
    reviews: PropTypes.shape({
      ratingAverage: PropTypes.number
    }),
    title: PropTypes.string,
    description: PropTypes.string,
    area: PropTypes.number,
    roomArea: PropTypes.number,
    numberOfBedrooms: PropTypes.number,
    numberOfBathrooms: PropTypes.number,
    type: PropTypes.string,
    url: PropTypes.string,
    visited: PropTypes.bool,
    allowsPermanentContract: PropTypes.bool,
    firstAvailableDate: PropTypes.string,
    offers: PropTypes.arrayOf(PropTypes.shape({})),
    plan: PropTypes.string,
    isBestChoice: PropTypes.bool,
    isInstantBooking: PropTypes.bool,
    isSelect: PropTypes.bool,
    billsIncluded: PropTypes.string,
    landlordId: PropTypes.string,
    landlordScoring: PropTypes.shape({
      score: PropTypes.number,
      totalBookingsClosed: PropTypes.number
    }),
    location: PropTypes.shape({
      street: PropTypes.string,
      city: PropTypes.string
    }),
    displayPrice: PropTypes.string // should be a number, but it's a string
  }).isRequired,
  moveIn: PropTypes.string,
  moveOut: PropTypes.string,
  flexibleMoveIn: PropTypes.bool,
  flexibleMoveOut: PropTypes.bool,
  onCardMouseEnter: PropTypes.func,
  onCardMouseLeave: PropTypes.func,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
  tracking: PropTypes.string,
  hasLazyLoad: PropTypes.bool,
  renderExtraContent: PropTypes.func,
  newTab: PropTypes.bool,
  showAvailableFromYear: PropTypes.bool,
  withTransparentBackground: PropTypes.bool,
  pageType: PropTypes.string,
  section: PropTypes.string,
  onImageChange: PropTypes.func,
  showDescription: PropTypes.bool,
  isBooked: PropTypes.bool,
  showFavorite: PropTypes.bool,
  linkTo: PropTypes.string,
  showSquaredMeters: PropTypes.bool,
  imageRatio: PropTypes.oneOf([
    HOMECARD_IMAGE_RATIOS.HOMECARD,
    HOMECARD_IMAGE_RATIOS.PANORAMIC
  ]),
  isEmbedded: PropTypes.bool,
  dataTest: PropTypes.string,
  imageClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  renderRightFloatingElements: PropTypes.func
};

export default memo(
  Homecard,
  (prev, next) =>
    prev.data.id === next.data.id &&
    prev.selected === next.selected &&
    prev.rentalTypes === next.rentalTypes
);
